import {
  Box, Flex, HStack, IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger,
  Portal, Text, useBreakpointValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/**
 * Viral share menu for a generated song. One component, used wherever
 * a generated track is surfaced (ResultStage after a generation, the
 * row menu on My Songs, the expanded FullPlayer).
 *
 * UX: a single Share trigger. On tap, a popover opens with one-tap
 * targets for WhatsApp / X / Facebook / Telegram / Reddit plus Copy
 * link. On mobile where `navigator.share` is available, we surface a
 * "Share…" item at the top that opens the OS share sheet — the native
 * sheet is the highest-converting surface on iOS/Android because the
 * recipient picker is already loaded.
 *
 * Instagram deliberately omitted: it has no public share-intent URL,
 * only an app-side action. "Copy link" is the correct Instagram path
 * (users paste into a Story / DM / bio).
 *
 * Share URL defaults to the public /s/:jobId page which plays without
 * auth. Caller passes the fully-qualified URL so the component is
 * agnostic to router/hosting.
 */
export function ShareMenu({
  jobId,
  title,
  shareUrl,
  variant = 'button',
  size = 'md',
}: {
  jobId: string;
  title: string;
  shareUrl: string;
  variant?: 'button' | 'pill' | 'icon';
  size?: 'sm' | 'md' | 'lg';
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const popover = useDisclosure();
  // On touch devices the OS share sheet converts higher than any
  // in-app list, so show it as the top option. Desktop users get
  // the social grid directly.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const isCompact = useBreakpointValue({ base: true, md: false });

  const shareText = t('share.text', 'Listen to "{{title}}" — made with MixGenie', { title });
  const encUrl = encodeURIComponent(shareUrl);
  const encText = encodeURIComponent(shareText);
  const encTextWithUrl = encodeURIComponent(`${shareText} ${shareUrl}`);

  const targets: Array<{
    key: string; label: string; href?: string; onClick?: () => void; bg: string;
    icon: JSX.Element;
  }> = [
    { key: 'whatsapp', label: 'WhatsApp', bg: '#25D366',
      href: `https://wa.me/?text=${encTextWithUrl}`,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/></svg>,
    },
    { key: 'x', label: 'X', bg: '#000000',
      href: `https://twitter.com/intent/tweet?url=${encUrl}&text=${encText}`,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
    },
    { key: 'facebook', label: 'Facebook', bg: '#1877F2',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encText}`,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    },
    { key: 'telegram', label: 'Telegram', bg: '#26A5E4',
      href: `https://t.me/share/url?url=${encUrl}&text=${encText}`,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.464.141a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>,
    },
    { key: 'reddit', label: 'Reddit', bg: '#FF4500',
      href: `https://www.reddit.com/submit?url=${encUrl}&title=${encText}`,
      icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12.3c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg>,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: t('share.linkCopied', 'Share link copied'), status: 'success', duration: 1800 });
    } catch {
      toast({ title: shareUrl, status: 'info', duration: 4000 });
    }
    popover.onClose();
  };

  const nativeShare = async () => {
    try {
      await (navigator as any).share({ title, text: shareText, url: shareUrl });
    } catch { /* user cancelled */ }
    popover.onClose();
  };

  // Trigger styles differ by variant so we fit into existing surfaces:
  // - button: a prominent brand-ghost pill (ResultStage, FullPlayer)
  // - pill: a chip-sized pill (secondary places)
  // - icon: a bare icon (compact row menus)
  const trigger = (() => {
    const label = t('share.cta', 'Share');
    if (variant === 'icon') {
      // Use a real Chakra IconButton size="sm" so the row stays visually
      // uniform with the sibling heart + kebab buttons (same 32×32 box,
      // same ghost/muted colour, same hover pop). An ad-hoc button with
      // its own padding reads larger even when it isn't, because the
      // sibling IconButtons compact their hit-area tightly.
      return (
        <IconButton
          aria-label={label}
          variant="ghost"
          color="mg.muted"
          _hover={{ color: 'pink.300', bg: 'mg.surfaceHi' }}
          size="sm"
          icon={<ShareGlyph size={18} />}
        />
      );
    }
    const bg = variant === 'pill' ? 'mg.surface' : 'mg.surface';
    const border = '1px solid';
    const h = size === 'lg' ? 11 : size === 'sm' ? 8 : 10;
    const px = size === 'lg' ? 5 : 3.5;
    const fs = size === 'lg' ? '14px' : '12.5px';
    return (
      <Flex
        as="button"
        align="center" justify="center" gap={1.5}
        h={h} px={px}
        bg={bg} color="mg.fg"
        border={border} borderColor="mg.line2"
        borderRadius={variant === 'pill' ? 'full' : '10px'}
        fontSize={fs} fontWeight={700}
        _hover={{ borderColor: '#ec4899', color: '#ec4899' }}
        transition="border-color .15s, color .15s"
      >
        <ShareGlyph /> <Text>{label}</Text>
      </Flex>
    );
  })();

  return (
    <Popover isOpen={popover.isOpen} onOpen={popover.onOpen} onClose={popover.onClose} placement="top" closeOnBlur>
      <PopoverTrigger>{trigger}</PopoverTrigger>
      <Portal>
        <PopoverContent
          bg="mg.surface" color="mg.fg" borderColor="mg.line2" borderRadius="14px"
          boxShadow="0 20px 50px -20px rgba(0,0,0,0.35)"
          w={{ base: '280px', md: '300px' }}
          _focus={{ outline: 'none', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.35)' }}
        >
          <PopoverArrow bg="mg.surface" />
          <PopoverBody p={3}>
            {canNativeShare && isCompact && (
              <>
                <Flex
                  as="button" w="100%" align="center" gap={2}
                  px={3} py={2.5} borderRadius="10px"
                  bg="mg.surfaceHi" _hover={{ bg: 'mg.line2' }}
                  onClick={nativeShare}
                >
                  <Box fontSize="18px" lineHeight={1} w="24px" textAlign="center">📱</Box>
                  <Text fontSize="13.5px" fontWeight={600}>{t('share.device', 'Share…')}</Text>
                </Flex>
                <Box h="1px" bg="mg.line2" my={2} />
              </>
            )}

            <Text fontSize="11px" fontWeight={700} letterSpacing="0.1em" textTransform="uppercase" color="mg.muted" mb={2} px={1}>
              {t('share.pickApp', 'Share via')}
            </Text>

            <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={1.5}>
              {targets.map((tgt) => (
                <Flex key={tgt.key} direction="column" align="center" gap={1}>
                  <Flex
                    as="a" href={tgt.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => { popover.onClose(); }}
                    align="center" justify="center"
                    w="42px" h="42px" borderRadius="full"
                    bg={tgt.bg} color="white"
                    _hover={{ transform: 'translateY(-1px)', filter: 'brightness(1.08)' }}
                    transition="transform .1s, filter .15s"
                    aria-label={tgt.label}
                  >
                    {tgt.icon}
                  </Flex>
                  <Text fontSize="10.5px" color="mg.muted" fontWeight={600}>{tgt.label}</Text>
                </Flex>
              ))}
            </Box>

            <Box h="1px" bg="mg.line2" my={3} />

            <Flex
              as="button" w="100%" align="center" gap={2}
              px={3} py={2.5} borderRadius="10px"
              bg="mg.surfaceHi" _hover={{ bg: 'mg.line2' }}
              onClick={copyLink}
            >
              <Box fontSize="16px" lineHeight={1} w="24px" textAlign="center">🔗</Box>
              <Box flex="1" minW={0}>
                <Text fontSize="13px" fontWeight={700}>{t('share.copyLink', 'Copy link')}</Text>
                <Text fontSize="11px" color="mg.muted" noOfLines={1}>{shareUrl.replace(/^https?:\/\//, '')}</Text>
              </Box>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

// The canonical three-dots-connected-by-lines share glyph. Chosen over
// the upload-arrow treatment because that one reads as "upload a file"
// to most users; this is the share icon people recognise from Android,
// Twitter, Instagram DM, etc. Rendered via currentColor so it inherits
// the trigger's text color (muted at rest, pink on hover).
function ShareGlyph({ size = 16 }: { size?: number }) {
  return (
    <Box as="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </Box>
  );
}

